import type { VariableType } from '@idc-forms/formula/types'
import { VARIABLE_SCOPE, type VariableMetadata, type VariableScope } from '../variable'

/**
 * EL CATÁLOGO DE DOCUMENTOS: las 128 variables escalares del motor de plantillas.
 *
 * Son el metadato —y sólo el metadato— de lo que hoy declara
 * `apps/web-real-estate/src/shared/variables/domains/*`. El `resolve(ctx)` de cada una SE QUEDA
 * allá, co-locado con la referencia a este metadato: el corte del ADR 0036 es metadato ≠
 * resolución, y ningún `resolve` se mueve.
 *
 * ⛔ Las dos colecciones iterables (`clientes`, `cuotas`) quedan FUERA del paquete, por decisión
 * del lead: Créditos no tiene nada iterable y declararlas compartidas sería prometer un «por cada»
 * que el correo no expande.
 */

/** De dónde sale el dato. Unión CERRADA — a diferencia de la de Créditos, que es el nombre de la etapa. */
export const DOCUMENT_VARIABLE_CATEGORY = {
  SOCIETY: 'society',
  PROJECT: 'project',
  PROPERTY: 'property',
  NEGOTIATION: 'negotiation',
  CURRENCY: 'currency',
  OWNER: 'owner',
  ADVISOR: 'advisor',
  DOCUMENT: 'document',
  OTHER: 'other'
} as const
export type DocumentVariableCategory =
  (typeof DOCUMENT_VARIABLE_CATEGORY)[keyof typeof DOCUMENT_VARIABLE_CATEGORY]

/**
 * Los tipos que USA el motor de documentos, angostados del vocabulario canónico.
 *
 * No es un vocabulario aparte: es `VariableType` menos lo que este catálogo no escribe. `email` y
 * `file` son de Créditos —un adjunto no se imprime en un contrato—, y el día que documentos
 * estrene uno se agrega acá, no en otra unión.
 */
export type DocumentVariableType = Extract<
  VariableType,
  'string' | 'number' | 'boolean' | 'date' | 'enum'
>

/** El metadato canónico, angostado a lo que este catálogo puede declarar. */
export type DocumentVariableMetadata = Omit<
  VariableMetadata,
  'category' | 'type' | 'example' | 'insertable'
> & {
  category: DocumentVariableCategory
  type: DocumentVariableType
  /** Documentos siempre trae el ejemplo como texto: es lo que se pinta en el panel de detalle. */
  example?: string
}

/**
 * Todas las de este catálogo aplican en `document` y sólo ahí: las resuelve el `resolve(ctx)` del
 * front contra la negociación cargada, en la impresión y en la vista previa del editor.
 *
 * El scope se declara UNA vez por catálogo y no 128 veces porque acá es uniforme y es verdad. Una
 * variable de documentos que algún día aplique en otro contexto lo declara ella, con su override.
 */
const DOCUMENT_SCOPES = [VARIABLE_SCOPE.DOCUMENT] as const

type DocumentVariableInput = Omit<DocumentVariableMetadata, 'scopes'> & {
  scopes?: readonly VariableScope[]
}

/** Declara una variable del catálogo de documentos. Le pone el scope y nada más. */
export const documentVariable = (input: DocumentVariableInput): DocumentVariableMetadata => ({
  ...input,
  scopes: input.scopes ?? DOCUMENT_SCOPES
})

/** Un campo de un grupo: lo mismo, menos lo que aporta el grupo (`key`, `category`, `group`). */
export type DocumentVariableGroupField = Omit<
  DocumentVariableInput,
  'key' | 'category' | 'group' | 'type'
> & {
  /** Default `string`: la mayoría de los campos de un objeto son texto. */
  type?: DocumentVariableType
}

/**
 * Expone un OBJETO del dominio como un grupo de variables punteadas (`prefijo.campo`),
 * declarándolo UNA sola vez. Es el espejo exacto de `defineObjectGroup` del host, que ahora
 * referencia estos metadatos y aporta los `resolve`.
 */
export const documentVariableGroup = (config: {
  prefix: string
  category: DocumentVariableCategory
  groupLabel: string
  fields: Record<string, DocumentVariableGroupField>
}): DocumentVariableMetadata[] =>
  Object.entries(config.fields).map(([key, field]) =>
    documentVariable({
      ...field,
      key: `${config.prefix}.${key}`,
      category: config.category,
      group: config.groupLabel,
      type: field.type ?? 'string'
    })
  )
